// src/ui/ScorePopManager.ts
// Phase 17 (UI-10) — staggered score pop animation with 4-cap.
// Replaces the fire-and-forget showScorePopup() helper in RoundScene.

import Phaser from 'phaser';
import { addCrispText } from './textHelper';

const MAX_VISIBLE = 4;
const HIT_COLOR = '#d4af37'; // Art Deco gold (Phase 17 palette)
const MISS_COLOR = '#888888';
const FLOAT_DIST = 48;  // px upward travel
const ANIM_MS = 800;    // total animation duration
const FADE_START = 600; // ms into animation when fade begins

export class ScorePopManager {
  private readonly scene: Phaser.Scene;
  private active: Phaser.GameObjects.Text[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Queue a pop for a single fiche.
   * @param x     World x of the fiche token center
   * @param y     World y of the fiche token top
   * @param total Score total (shown as "+N" or "miss")
   * @param hit   true = hit (gold), false = miss (grey)
   * @param delay ms to wait before starting this pop's animation
   */
  pop(x: number, y: number, total: number, hit: boolean, delay: number): void {
    // Enforce 4-cap: destroy oldest if at limit
    if (this.active.length >= MAX_VISIBLE) {
      const oldest = this.active.shift()!;
      oldest.destroy();
    }

    const label = hit ? `+${total}` : 'miss';
    const color = hit ? HIT_COLOR : MISS_COLOR;

    // Placeholder holds the cap slot until text is created in onStart.
    // This prevents a 5th simultaneous pop from bypassing the cap before animations begin.
    const placeholder = {} as Phaser.GameObjects.Text;
    this.active.push(placeholder);

    let text: Phaser.GameObjects.Text;

    // Use a dummy tween as a delay timer — text is created in onStart so it only
    // appears when its animation actually begins (prevents stationary ghost labels
    // during the delay window before earlier fiches finish their stagger).
    this.scene.tweens.add({
      targets: { _d: 0 },
      _d: 1,
      duration: ANIM_MS,
      delay,
      onStart: () => {
        text = addCrispText(this.scene, x, y, label, {
          color,
          fontSize: '16px',
        }).setOrigin(0.5);
        // Replace placeholder with real text object in cap list
        const capIdx = this.active.indexOf(placeholder);
        if (capIdx !== -1) this.active[capIdx] = text;
        // Float upward
        this.scene.tweens.add({
          targets: text,
          y: y - FLOAT_DIST,
          duration: ANIM_MS,
          ease: 'Linear',
        });
        // Fade out after FADE_START ms
        this.scene.tweens.add({
          targets: text,
          alpha: 0,
          duration: ANIM_MS - FADE_START,
          delay: FADE_START,
          ease: 'Linear',
        });
      },
      onComplete: () => {
        const idx = this.active.indexOf(text ?? placeholder);
        if (idx !== -1) this.active.splice(idx, 1);
        text?.destroy();
      },
    });
  }

  /** Force-destroy all active pops immediately. Call on scene shutdown. */
  clear(): void {
    this.active.forEach((t) => t.destroy());
    this.active = [];
  }
}
